<template>
  <!-- Container for the registration form -->
  <div class="max-w-md mx-auto bg-white border border-solid rounded-lg shadow-lg border-neutral-300">
    <!-- Header section of the form -->
    <div class="p-4 bg-white border-b border-solid rounded-t-lg border-neutral-300">
      <h3 class="text-lg font-semibold text-neutral-800">Opret konto</h3>
    </div>
    <!-- Body section of the form -->
    <div class="p-4">
      <!-- Form element with submit handler -->
      <form class="space-y-4" @submit.prevent="register" autocomplete="off">
        <!-- Username input field -->
        <div>
          <label for="username" class="block mb-1.5 text-sm font-medium text-neutral-900">Brugernavn</label>
          <input
            type="text"
            name="username"
            id="username"
            v-model="registrationStore.formData.username"
            class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
            :class="{ 'border-red-500': formErrors.username }"
            placeholder="Indtast et brugernavn"
            required
          />
          <p v-if="formErrors.username" class="mt-1 text-sm text-red-600">{{ formErrors.username }}</p>
        </div>
        <!-- Email input field -->
        <div>
          <label for="email" class="block mb-1.5 text-sm font-medium text-neutral-900">Email</label>
          <input
            type="email"
            name="email"
            id="email"
            v-model="registrationStore.formData.email"
            class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
            :class="{ 'border-red-500': formErrors.email }"
            placeholder="Indtast din @km.dk email adresse"
            required
          />
          <p v-if="formErrors.email" class="mt-1 text-sm text-red-600">{{ formErrors.email }}</p>
        </div>
        <!-- First and Last Name input fields -->
        <div class="flex space-x-4">
          <div class="flex-1">
            <label for="firstName" class="block mb-1.5 text-sm font-medium text-neutral-900">Fornavn</label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              v-model="registrationStore.formData.firstName"
              class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
              :class="{ 'border-red-500': formErrors.firstName }"
              placeholder="Indtast dit fornavn"
              required
            />
            <p v-if="formErrors.firstName" class="mt-1 text-sm text-red-600">{{ formErrors.firstName }}</p>
          </div>
          <div class="flex-1">
            <label for="lastName" class="block mb-1.5 text-sm font-medium text-neutral-900">Efternavn</label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              v-model="registrationStore.formData.lastName"
              class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
              :class="{ 'border-red-500': formErrors.lastName }"
              placeholder="Indtast dit efternavn"
              required
            />
            <p v-if="formErrors.lastName" class="mt-1 text-sm text-red-600">{{ formErrors.lastName }}</p>
          </div>
        </div>
        <!-- Phone Number input field -->
        <div>
          <label for="phoneNumber" class="block mb-1.5 text-sm font-medium text-neutral-900">Telefonnummer</label>
          <input
            type="tel"
            name="phoneNumber"
            id="phoneNumber"
            v-model="registrationStore.formData.phoneNumber"
            class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
            :class="{ 'border-red-500': formErrors.phoneNumber }"
            placeholder="Indtast dit telefonnummer"
            required
          />
          <p v-if="formErrors.phoneNumber" class="mt-1 text-sm text-red-600">{{ formErrors.phoneNumber }}</p>
        </div>
        <!-- Password input field -->
        <div>
          <label for="password" class="block mb-1.5 text-sm font-medium text-neutral-900">Kodeord</label>
          <input
            type="password"
            name="password"
            id="password"
            v-model="registrationStore.formData.password"
            placeholder="Indtast et kodeord"
            class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
            :class="{ 'border-red-500': formErrors.password }"
            required
            autocomplete="new-password"
          />
          <p v-if="formErrors.password" class="mt-1 text-sm text-red-600">{{ formErrors.password }}</p>
        </div>
        <!-- Repeat Password input field -->
        <div>
          <label for="repeatPassword" class="block mb-1.5 text-sm font-medium text-neutral-900">Gentag kodeord</label>
          <input
            type="password"
            name="repeatPassword"
            id="repeatPassword"
            v-model="repeatPassword"
            placeholder="Gentag kodeordet"
            class="bg-gray-50 border border-neutral-300 text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5"
            :class="{ 'border-red-500': formErrors.repeatPassword }"
            required
            autocomplete="new-password"
          />
          <p v-if="formErrors.repeatPassword" class="mt-1 text-sm text-red-600">{{ formErrors.repeatPassword }}</p>
        </div>
        <!-- Submit button -->
        <button
          type="submit"
          :disabled="registrationStore.isLoading"
          class="w-full text-white bg-green-600 hover:bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
        >
          Registrer
        </button>
        <!-- Login link -->
        <div class="text-sm font-medium text-neutral-700">
          Allerede registreret?
          <a href="#" class="text-blue-700 hover:underline" @click.prevent="emit('show-login')">Log ind</a>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, inject } from "vue";

// Define custom events to emit
const emit = defineEmits(["registration-success", "show-login"]);

// Inject the registrationStore from the parent component or provide
const registrationStore = inject("registrationStore");

// Define reactive variables for form inputs and errors
const repeatPassword = ref("");
const formErrors = ref({});

// Computed property to check if passwords match
const passwordsMatch = computed(() => {
  return registrationStore.formData.password === repeatPassword.value;
});

// Function to validate the form inputs
const validateForm = () => {
  formErrors.value = {};
  if (!registrationStore.formData.username) {
    formErrors.value.username = "Brugernavn er påkrævet";
  }
  if (!registrationStore.formData.email) {
    formErrors.value.email = "Email er påkrævet";
  }
  if (!registrationStore.formData.firstName) {
    formErrors.value.firstName = "Fornavn er påkrævet";
  }
  if (!registrationStore.formData.lastName) {
    formErrors.value.lastName = "Efternavn er påkrævet";
  }
  if (!registrationStore.formData.phoneNumber) {
    formErrors.value.phoneNumber = "Telefonnummer er påkrævet";
  }
  if (!registrationStore.formData.password) {
    formErrors.value.password = "Kodeord er påkrævet";
  }
  if (!repeatPassword.value) {
    formErrors.value.repeatPassword = "Gentag kodeord er påkrævet";
  } else if (!passwordsMatch.value) {
    formErrors.value.repeatPassword = "Kodeordene matcher ikke";
  }
  return Object.keys(formErrors.value).length === 0;
};

// Function to handle registration
const register = async () => {
  if (!validateForm()) {
    return;
  }
  const success = await registrationStore.register();
  if (success) {
    emit("show-login");
  }
};
</script>